import {CSSProperties, ReactNode, useEffect, useRef, useState} from 'react';

interface Option {
    value: string;
    label?: string;
    title?: string;
}

interface OptionProps {
    option: Option;
    selectedOption: Option | null;
    handleOptionClick: (o: Option['value']) => void;
    index: number;
}

type OptionComponentType = (props: OptionProps) => ReactNode;

const DefaultOptionComponent: OptionComponentType = ({
    option,
    selectedOption,
    handleOptionClick,
    index,
}) => {
    const isSelected = option.value === selectedOption?.value;
    return (
        <div
            key={index}
            className={`option ${isSelected ? 'selected' : ''}`}
            onClick={() => handleOptionClick(option.value)}
        >
            {option.label ?? option.value}
        </div>
    );
};

const FontOption: OptionComponentType = ({
    option,
    handleOptionClick,
    index,
}: OptionProps) => {
    return (
        <div
            key={index}
            className="option"
            onClick={() => handleOptionClick(option.value)}
        >
            <span style={{fontFamily: option.value}}>{option.label}</span>
        </div>
    );
};

const TitleCapitalizationOption: OptionComponentType = ({
    option,
    handleOptionClick,
}: OptionProps) => {
    return (
        <div
            className="option headerCapitalization--option"
            style={{
                textTransform:
                    option.value as React.CSSProperties['textTransform'],
            }}
            onClick={() => handleOptionClick(option.value)}
        >
            {option.value}
        </div>
    );
};

/**
 * A custom filterable select component to replace react-select
 */
const Select = ({
    options,
    value,
    popoutWindow,
    onChange = () => {},
    placeholder = 'Select...',
    OptionComponent = DefaultOptionComponent,
    style = {},
}: {
    options: Option[];
    value: Option['value'] | null;
    onChange?: (o: Option['value']) => void;
    placeholder?: string;
    OptionComponent?: typeof DefaultOptionComponent;
    style?: CSSProperties;
    popoutWindow: Window;
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [filterText, setFilterText] = useState('');
    const [filteredOptions, setFilteredOptions] = useState(options || []);
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // Filter options when input changes
    useEffect(() => {
        if (!options) return;

        if (!filterText) {
            setFilteredOptions(options);
        } else {
            const filtered = options.filter(option =>
                option.label?.toLowerCase().includes(filterText.toLowerCase())
            );
            setFilteredOptions(filtered);
        }
    }, [filterText, options]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                containerRef.current &&
                !containerRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false);
            }
        };

        const handleEscapePressed = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setIsOpen(false);
            }
        };

        // Try both mousedown and click events
        const currentDocument = popoutWindow?.document ?? document;
        currentDocument.addEventListener('mousedown', handleClickOutside);

        currentDocument.addEventListener('keydown', handleEscapePressed);

        return () => {
            currentDocument.removeEventListener(
                'mousedown',
                handleClickOutside
            );
            currentDocument.removeEventListener('keydown', handleEscapePressed);
        };
    }, []);

    // Focus input when dropdown opens
    useEffect(() => {
        if (isOpen && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isOpen]);

    const toggleDropdown = () => {
        setIsOpen(!isOpen);
        setFilterText('');
        setFilteredOptions(options);
    };

    const handleOptionClick = (option: Option['value']) => {
        onChange(option);
        setIsOpen(false);
        setFilterText('');
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFilterText(e.target.value);
    };

    // Find the currently selected option
    const selectedOption =
        options && options.length > 0
            ? options.find(option => option.value === value) || options[0]
            : null;

    const Option: OptionComponentType =
        OptionComponent || DefaultOptionComponent;

    return (
        <div
            ref={containerRef}
            style={style}
            className="custom-select"
            data-testid="select-container"
        >
            <div
                className="select-value"
                style={{display: isOpen ? 'none' : 'flex'}}
                onClick={toggleDropdown}
            >
                <span>
                    {selectedOption
                        ? (selectedOption.label ?? selectedOption.value)
                        : placeholder}
                </span>
                <span className="select-arrow"></span>
            </div>

            <div
                className="input-container"
                style={{display: isOpen ? 'block' : 'none'}}
            >
                <input
                    ref={inputRef}
                    type="text"
                    value={filterText}
                    onChange={handleInputChange}
                    className="search-input"
                    placeholder="Type to search..."
                />
            </div>

            <div
                className="dropdown"
                style={{display: isOpen ? 'block' : 'none'}}
            >
                {filteredOptions.length > 0 ? (
                    filteredOptions.map((option, index) => (
                        <Option
                            key={index}
                            option={option}
                            selectedOption={selectedOption}
                            handleOptionClick={handleOptionClick}
                            index={index}
                        />
                    ))
                ) : (
                    <div className="no-results">No results found</div>
                )}
            </div>
        </div>
    );
};

export {FontOption, Select, TitleCapitalizationOption};
